"use client";
import React, { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import {
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useFormContext } from "./FormContext";
import toast from "react-hot-toast";
import { registerUser } from "@/store/slices/onboardingSlice";
import { useAppDispatch, useAppSelector } from "@/hooks/useAppSelector";
import { fetchUser, setBulkUserID } from "@/store/slices/authSlice";
import { activateLOC } from "@/store/slices/paymentSlice";
import { FinalData } from "@/types/onboarding";
import { useRouter } from "next/navigation";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { setCookie } from "cookies-next";
import { HourglassTop } from "@mui/icons-material";
import PostOnboardingMessage from "./PostOnboardingModal";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;
const validReferralCode = ["JOINCCNOW", "JOIN2025"];

const KycDetails = ({ onBack }: { onBack: () => void }) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { finalData, setFinalData } = useFormContext();
  const [load, setLoad] = useState<boolean>(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
    getValues,
    watch,
  } = useForm({
    defaultValues: finalData,
  });

  const [loading, setLoading] = useState({
    PAN: false,
    Aadhar: false,
    GST: false,
  });
  const { creditLimit, phoneNumber } = useAppSelector((state) => state.auth);
  // console.log(creditLimit);
  const [panVerified, setPANVerified] = useState(false);
  const [gstVerified, setGSTVerified] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [openPostOnboardingModal, setOpenPostOnboardingModal] = useState(false);

  const handleClosePostOnboardMessage = () => {
    router.push("/auth");
  };

  const [applicable, setApplicable] = useState("");
  const pan_card = watch("pan_card");
  const GST = watch("GST");
  const aadhar = watch("aadhar");

  useEffect(() => {
    Object.keys(finalData).forEach((key) => {
      setValue(key as keyof FinalData, finalData[key as keyof FinalData]);
    });
  }, [finalData, setValue]);

  const checkExistence = async (
    type: keyof FinalData,
    value: string,
    url: string
  ) => {
    setLoading((prev) => ({ ...prev, [type]: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/${url}/${value}`);
      const data = await response.json();

      if (data.status === "error") {
        setError(type, {
          type: "manual",
          message: `${type} already registered with Cyclecircle`,
        });
      } else {
        if (type === "pan_card") {
          //   handleVerifyPAN();
          setPANVerified(true);
          setError(type, {
            type: "manual",
            message: ``,
          });
        }
        if (type === "GST") {
          //   handleVerifyGST();
          setGSTVerified(true);
          setError(type, {
            type: "manual",
            message: ``,
          });
        }
        setError(type, { type: "manual", message: "" });
      }
    } catch (error) {
      console.error(`Error checking ${type} existence:`, error);
    } finally {
      setLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  const debounce = <T extends (...args: Parameters<T>) => Promise<void> | void>(
    func: T,
    delay: number
  ) => {
    let timer: ReturnType<typeof setTimeout>;

    return (...args: Parameters<T>) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const handleBlur = useCallback(
    debounce(async (type: keyof FinalData, value: string) => {
      if (!value) return;

      // const validators = {
      //   pan_card: validatePAN,
      //   Aadhar: validateAadhaar,
      //   GST: validateGSTIN,
      // };

      // if (!validators[type as "pan_card" | "Aadhar" | "GST"](value)) {
      //   setError(type, {
      //     type: "manual",
      //     message: `Invalid ${type} format`,
      //   });

      //   return;
      // }
      if (type === "pan_card") {
        await checkExistence(type, value, `singup_pan_verify`);
      } else {
        await checkExistence(
          type,
          value,
          `singup_${type.toLowerCase()}_verify`
        );
      }
    }, 500),
    []
  );
  const handleApplyReferral = () => {
    const code = getValues("applicable").toUpperCase().trim();
    if (validReferralCode.includes(code) && code !== "1") {
      setApplicable("Valid Referral Code Applied");
    } else {
      setApplicable("Invalid Referral Code");
    }
  };

  const isFormValid = () => {
    return (
      pan_card?.length === 10 &&
      panVerified &&
      GST?.length === 15 &&
      gstVerified &&
      aadhar?.length === 12 &&
      agreeTerms
    );
  };

  const [statusStep, setStatusStep] = useState<number>(0);

  const statusMessages = [
    "Your Details are being fetched...",
    "Activating Credit...",
    "Redirecting...",
  ];

  useEffect(() => {
    if (pan_card?.length === 10) {
      handleBlur("pan_card", pan_card);
    }

    if (aadhar?.length === 12) {
      handleBlur("aadhar", aadhar);
    }

    if (GST?.length === 15) {
      handleBlur("GST", GST);
    }
  }, [aadhar, pan_card, GST, handleBlur]);

  const onSubmit = async (data: FinalData) => {
    setFinalData(data);
    setLoad(true);

    try {
      // 1️⃣ Register User
      const response = await dispatch(registerUser(data));

      if (!response?.payload) {
        toast.error("Signup failed. Please try again later.");
        throw new Error("Signup failed");
      }

      // console.log(response, response?.payload?.id);

      // 2️⃣ Set Cookie & Update Redux Store
      if (response?.payload?.id) {
        setLoad(false);
        if (response?.payload?.message) {
          toast.success("Signup successful");
        }
        if (!phoneNumber) {
          setOpenPostOnboardingModal(true);
          return;
        }
        setShowSuccess(true);

        await new Promise<void>((resolve) => {
          setCookie("bulkUserID", response?.payload?.id, {
            maxAge: 60 * 60 * 24 * 7, // 7 days
            path: "/",
            secure: process.env.NEXT_PUBLIC_NODE_ENV === "production",
            httpOnly: false, // Set to true if using server-side authentication
          });
          dispatch(setBulkUserID(String(response?.payload?.id)));
          resolve();
        });

        // 👇 Sequentially update status step every 3 seconds
        for (let step = 0; step < statusMessages.length; step++) {
          setStatusStep(step);
          await new Promise((resolve) => setTimeout(resolve, 3000));
        }

        // 3️⃣ Fetch User Details (After Cookie is Set)
        await dispatch(fetchUser(String(response?.payload?.id)));

        // 4️⃣ Activate LOC if applicable
        if (creditLimit !== null && Number(creditLimit) > 0) {
          const creditData = {
            UserID: response?.payload?.id,
            creditLimit: parseInt(creditLimit),
          };
          await dispatch(activateLOC(creditData));
        }

        // 5️⃣ Redirect to /checkout/cart without intermediate homepage
        router.push("/checkout/cart");
      }
    } catch (error) {
      console.error("Error signing up:", error);
      setLoad(false);
    }
  };

  return (
    <>
      {showSuccess ? (
        <div className="w-full h-[500px] flex flex-col items-center justify-center bg-white-1 z-50">
          <div className="flex flex-col items-center gap-4">
            {statusStep === 3 ? (
              <CheckCircleIcon
                className="text-green"
                style={{ fontSize: "100px" }}
              />
            ) : (
              <HourglassTop
                className="text-blue animate-spin"
                style={{ fontSize: "80px" }}
              />
            )}

            <p className="text-xl font-[Raleway] font-semibold text-blue tracking-wider">
              {statusMessages[statusStep] || "Processing..."}
            </p>

            {statusStep < 3 && (
              <CircularProgress size={50} className="text-blue-500 mt-4" />
            )}
          </div>
        </div>
      ) : openPostOnboardingModal ? (
        <PostOnboardingMessage
          handleClosePostOnboardMessage={handleClosePostOnboardMessage}
        />
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full max-h-full space-y-4"
        >
          <h2 className="text-2xl font-bold text-gray-900">KYC Details</h2>
          <span className="text-gray-500 text-sm tracking-wide">
            Please provide your business KYC information
          </span>

          <TextField
            label="Referral Code"
            variant="outlined"
            fullWidth
            {...register("applicable")}
            inputProps={{
              style: { textTransform: "uppercase" },
            }}
            onChange={(e) => {
              // Remove spaces and convert to uppercase
              const newValue = e.target.value.replace(/\s/g, "").toUpperCase();
              e.target.value = newValue;
              setValue("applicable", newValue, { shouldValidate: true });
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    onClick={handleApplyReferral}
                    variant="contained"
                    size="small"
                    sx={{
                      backgroundColor:
                        applicable.includes("Invalid") &&
                        !applicable.includes("")
                          ? "var(--color-green)"
                          : "var(--color-blue)",
                    }}
                  >
                    {!applicable.includes("Invalid") && !applicable.includes("")
                      ? "Applied"
                      : "Apply"}
                  </Button>
                </InputAdornment>
              ),
            }}
          />
          {applicable && (
            <p
              className={`text-[12px] !mt-1 ${
                applicable.includes("Invalid") ? "text-peach" : "text-green"
              }`}
            >
              {applicable}
            </p>
          )}

          <TextField
            label="PAN Card"
            variant="outlined"
            fullWidth
            {...register("pan_card", {
              required: "PAN is required",
              pattern: {
                value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                message: "",
              },
              setValueAs: (value) => value?.toUpperCase(),
            })}
            onBlur={(e) => handleBlur("pan_card", e.target.value)}
            error={
              !!errors.pan_card || (!panVerified && pan_card?.length === 10)
            }
            helperText={
              errors.pan_card?.message
                ? errors.pan_card?.message
                : !panVerified && pan_card?.length === 10
                ? "Enter a valid PAN"
                : panVerified &&
                  pan_card?.length === 10 && (
                    <span
                      style={{ color: "var(--color-green)", fontWeight: "600" }}
                    >
                      PAN Verified ✅
                    </span>
                  )
            }
            InputProps={{
              endAdornment: loading.PAN && (
                <CircularProgress
                  size={30}
                  sx={{ color: "var(--color-blue)" }}
                />
              ),
            }}
            inputProps={{
              maxLength: 10,
              style: { textTransform: "uppercase" },
            }}
            onChange={(e) => {
              // Convert input to uppercase and allow only alphanumeric characters
              const newValue = e.target.value
                .toUpperCase()
                .replace(/[^A-Z0-9]/g, "");
              e.target.value = newValue;
              setValue("pan_card", newValue, { shouldValidate: true });
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: panVerified ? "green" : "rgba(0, 0, 0, 0.23)", // Default border
                },
                "&:hover fieldset": {
                  borderColor: panVerified ? "green" : "#1976d2", // Hover state
                },
                "&.Mui-focused fieldset": {
                  borderColor: panVerified ? "green" : "#1976d2", // Focus state
                  borderWidth: "2px",
                },
              },
              "& .MuiInputLabel-root": {
                color: panVerified ? "inherit" : "#666", // Keep label color unchanged
              },
              "& .MuiInputLabel-root.Mui-error": {
                color: "#d32f2f", // Error label color
              },
            }}
          />

          <TextField
            label="Aadhar"
            variant="outlined"
            fullWidth
            {...register("aadhar", {
              required: "Aadhar number is required",
              pattern: {
                value: /^[2-9]{1}[0-9]{11}$/,
                message: "Enter a valid 12-digit Aadhar number",
              },
            })}
            onBlur={(e) => handleBlur("aadhar", e.target.value)}
            error={!!errors.aadhar}
            helperText={errors.aadhar?.message}
            InputProps={{
              endAdornment: loading.Aadhar && (
                <CircularProgress
                  size={20}
                  sx={{ color: "var(--color-blue)" }}
                />
              ),
            }}
            inputProps={{
              maxLength: 12,
              inputMode: "numeric",
            }}
            onChange={(e) => {
              // Remove spaces and non-numeric characters
              const newValue = e.target.value?.replace(/\D/g, ""); // Removes non-numeric characters
              e.target.value = newValue;
              setValue("aadhar", newValue, { shouldValidate: true });
            }}
            onKeyPress={(e) => {
              // Prevent entering non-numeric characters
              if (!/[0-9]/.test(e.key)) {
                e.preventDefault();
              }
            }}
          />

          <TextField
            label="GST"
            variant="outlined"
            fullWidth
            {...register("GST", {
              required: "GST is required",
              pattern: {
                value:
                  /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/,
                message: "Enter a valid GSTIN",
              },
              setValueAs: (value) => value?.toUpperCase(),
            })}
            onBlur={(e) => handleBlur("GST", e.target.value)}
            error={!!errors.GST || (!gstVerified && GST?.length === 15)}
            helperText={
              errors.GST?.message
                ? errors.GST?.message
                : !gstVerified && GST?.length === 15
                ? "Enter a valid GST"
                : gstVerified &&
                  GST?.length === 15 && (
                    <span
                      style={{ color: "var(--color-green)", fontWeight: "600" }}
                    >
                      GST Verified ✅
                    </span>
                  )
            }
            InputProps={{
              endAdornment: loading.GST && (
                <CircularProgress
                  size={20}
                  sx={{ color: "var(--color-blue)" }}
                />
              ),
            }}
            inputProps={{
              maxLength: 15,
              style: { textTransform: "uppercase" },
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
              />
            }
            label={
              <span className="text-black-1 tracking-wide">
                I hereby confirm that the provided details are accurate.
              </span>
            }
          />

          <div className="flex gap-4">
            <Button
              variant="outlined"
              fullWidth
              onClick={onBack}
              sx={{
                borderColor: "var(--color-blue)",
                color: "var(--color-blue)",
              }}
            >
              Back
            </Button>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                backgroundColor: "var(--color-blue)",
              }}
              disabled={!isFormValid()}
            >
              {load ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                "Submit"
              )}
            </Button>
          </div>
        </form>
      )}
    </>
  );
};

export default KycDetails;
